//Proofed
import { Box, Container, Grid, Typography, Stack, Chip, Card, CardContent, IconButton } from '@mui/material'
import { FancyGoldButton } from '../../components/FancyGoldButton'
import { FancyLightButton } from '../../components/FancyLightButton'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { COLOR_PRIMARY, NAVBAR_BG, SOLUTIONS_HERO_START, SOLUTIONS_HERO_MID } from '../../theme'
import { WaveDivider } from '../../components/WaveDivider'
import { CtaSection } from '../../components/CtaSection'
import PageMeta from '../../components/PageMeta'
import { BookDemoModal } from '../../components/BookDemoModal'
import { useState, useRef, useMemo } from 'react'

import { bidPartners, bdPartners, activePartners } from '../../common/NetworkBusinessWinningNetworkContent'


function PartnerCard({ name, domain, logo, logoBg, blurb }: { name: string; domain: string; logo: string | null; logoBg?: string; blurb?: string }) {
    const [imgFailed, setImgFailed] = useState(false)
    const initials = name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
    const showInitials = !logo || imgFailed

    return (
        <Card variant="outlined" component="a"
            href={`https://${domain}`} target="_blank" rel="noopener noreferrer"
            sx={{
                height: '100%',
                borderColor: 'rgba(0,0,0,0.08)',
                transition: 'all 0.2s',
                textDecoration: 'none',
                display: 'block',
                cursor: 'pointer',
                '&:hover': {
                    borderColor: COLOR_PRIMARY,
                    boxShadow: `0 4px 20px ${COLOR_PRIMARY}20`,
                    transform: 'translateY(-2px)',
                },
            }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* Logo + name row */}
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Box sx={{
                        width: 56, height: 56, flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        borderRadius: 1, overflow: 'hidden',
                        backgroundColor: showInitials ? `${COLOR_PRIMARY}15` : (logoBg ?? '#f9f9f9'),
                        border: '1px solid rgba(0,0,0,0.06)',
                    }}>
                        {showInitials ? (
                            <Typography sx={{ fontWeight: 800, fontSize: '18px', color: COLOR_PRIMARY }}>
                                {initials}
                            </Typography>
                        ) : (
                            <Box
                                component="img"
                                src={logo!}
                                alt={name}
                                onError={() => setImgFailed(true)}
                                sx={{
                                    width: 56,
                                    // border: BORDER_BLACK,
                                    objectFit: 'fill'
                                }}
                            />
                        )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontWeight={700} sx={{ fontSize: '14px', lineHeight: 1.3 }}>
                            {name}
                        </Typography>
                        <Typography sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, fontSize: '11px', color: 'text.disabled' }}>
                            {domain}
                            <OpenInNewIcon sx={{ fontSize: 10 }} />
                        </Typography>
                    </Box>
                </Stack>

                {/* Blurb */}
                {blurb && (
                    <Typography sx={{ fontSize: '13px', color: 'text.secondary', lineHeight: 1.7, flexGrow: 1 }}>
                        {blurb}
                    </Typography>
                )}
            </CardContent>
        </Card>
    )
}

const CARD_WIDTH = 300
const CARD_GAP = 20

function BidPartnersCarousel({ title, description, chipLabel, partners }: {
    title: string; description: string; chipLabel: string
    partners: { name: string; domain: string; logo: string | null; logoBg?: string; blurb?: string }[]
}) {
    const shuffled = useMemo(() => [...partners].sort(() => Math.random() - 0.5), [])
    const scrollRef = useRef<HTMLDivElement>(null)

    const scroll = (dir: 'left' | 'right') => {
        if (!scrollRef.current) return
        scrollRef.current.scrollBy({ left: dir === 'right' ? (CARD_WIDTH + CARD_GAP) * 2 : -(CARD_WIDTH + CARD_GAP) * 2, behavior: 'smooth' })
    }

    const initScrollRef = useRef(false)
    const setScrollRef = (el: HTMLDivElement | null) => {
        (scrollRef as React.MutableRefObject<HTMLDivElement | null>).current = el
        if (el && !initScrollRef.current) {
            initScrollRef.current = true
            const mid = (el.scrollWidth - el.clientWidth) / 2
            el.scrollLeft = mid
        }
    }

    return (
        <Box>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
                <Chip label={chipLabel} size="small" sx={{
                    backgroundColor: `${COLOR_PRIMARY}15`, color: '#856400',
                    border: `1px solid ${COLOR_PRIMARY}50`, fontWeight: 700,
                }} />
            </Stack>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h3" sx={{ fontSize: { xs: '22px', md: '28px' }, mb: 1, lineHeight: 1.3 }}>
                    {title}
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: '15px', lineHeight: 1.75, maxWidth: 600 }}>
                    {description}
                </Typography>
            </Box>

            {/* Carousel wrapper with arrows on sides */}
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'stretch', gap: 2 }}>
                {/* Left arrow */}
                <IconButton onClick={() => scroll('left')} sx={{
                    flexShrink: 0, width: 48, height: 'auto', borderRadius: 2, alignSelf: 'stretch',
                    bgcolor: 'white',
                    border: `1px solid rgba(0,0,0,0.12)`,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transition: 'all 0.2s',
                    '&:hover': {
                        bgcolor: COLOR_PRIMARY, borderColor: COLOR_PRIMARY,
                        color: 'white', boxShadow: `0 4px 16px ${COLOR_PRIMARY}40`,
                    },
                }}>
                    <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
                </IconButton>

                {/* Scroll track with fade hints */}
                <Box sx={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
                    {/* Left fade */}
                    <Box sx={{
                        position: 'absolute', left: 0, top: 0, bottom: 0, width: 20, zIndex: 1, pointerEvents: 'none',
                        background: 'linear-gradient(to right, white 0%, transparent 100%)',
                    }} />
                    {/* Right fade */}
                    <Box sx={{
                        position: 'absolute', right: 0, top: 0, bottom: 0, width: 20, zIndex: 1, pointerEvents: 'none',
                        background: 'linear-gradient(to left, white 0%, transparent 100%)',
                    }} />
                    <Box
                        ref={setScrollRef}
                        sx={{
                            display: 'flex',
                            gap: `${CARD_GAP}px`,
                            overflowX: 'auto',
                            scrollSnapType: 'x mandatory',
                            py: 2,
                            scrollbarWidth: 'auto',
                            scrollbarColor: `${COLOR_PRIMARY} rgba(0,0,0,0.08)`,
                            '&::-webkit-scrollbar': { height: 8 },
                            '&::-webkit-scrollbar-track': { background: 'rgba(0,0,0,0.06)', borderRadius: 8 },
                            '&::-webkit-scrollbar-thumb': { background: COLOR_PRIMARY, borderRadius: 8, border: '1px solid rgba(255,255,255,0.3)' },
                            '&::-webkit-scrollbar-thumb:hover': { background: `${COLOR_PRIMARY}cc` },
                        }}
                    >
                        {shuffled.map(p => (
                            <Box key={p.domain} sx={{ minWidth: CARD_WIDTH, maxWidth: CARD_WIDTH, scrollSnapAlign: 'start', flexShrink: 0 }}>
                                <PartnerCard name={p.name} domain={p.domain} logo={p.logo} logoBg={p.logoBg} blurb={p.blurb} />
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Right arrow */}
                <IconButton onClick={() => scroll('right')} sx={{
                    flexShrink: 0, width: 48, height: 'auto', alignSelf: 'stretch',
                    bgcolor: 'white',
                    border: `1px solid rgba(0,0,0,0.12)`,
                    borderRadius: 2,
                    boxShadow: `0 2px 8px ${COLOR_PRIMARY}40`,
                    transition: 'all 0.2s',
                    '&:hover': {
                        bgcolor: COLOR_PRIMARY, borderColor: COLOR_PRIMARY,
                        color: 'white', boxShadow: `0 4px 16px ${COLOR_PRIMARY}40`,
                    },
                }}>
                    <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>
        </Box>
    )
}


export default function BusinessWinningNetwork() {
    const [demoOpen, setDemoOpen] = useState(false)

    return (
        <Box>
            <PageMeta
                title="Business Winning Network"
                description="Find trusted UK bid writing partners and business development specialists through the CFP Business Winning Network. Experienced professionals across construction, IT, professional services and more."
                canonical="/success-network/business-winning-network"
            />
            {/* ── Hero ─────────────────────────────────────────────────────────── */}
            <Box sx={{
                background: `linear-gradient(135deg, ${SOLUTIONS_HERO_START} 0%, ${SOLUTIONS_HERO_MID} 100%)`,
                pt: { xs: 8, md: 12 },
                pb: { xs: 8, md: 10 },
                textAlign: 'center',
            }}>
                <Container maxWidth="md">
                    <Chip label="Business Winning Network · Partners" size="small" sx={{
                        mb: 3, backgroundColor: `${COLOR_PRIMARY}15`, color: COLOR_PRIMARY,
                        border: `1px solid ${COLOR_PRIMARY}40`, fontWeight: 700,
                    }} />
                    <Typography variant="h1" sx={{
                        color: 'white',
                        fontSize: { xs: '34px', md: '52px' },
                        lineHeight: 1.15, mb: 3,
                    }}>
                        Our{' '}
                        <Box component="span" sx={{ color: COLOR_PRIMARY }}>Business Winning Partners</Box>
                    </Typography>
                    <Typography sx={{
                        color: 'rgba(255,255,255,0.65)',
                        fontSize: { xs: '16px', md: '18px' },
                        lineHeight: 1.75, mb: 5, maxWidth: 600, mx: 'auto',
                    }}>
                        Contract Finder Pro works alongside a network of specialist bid writers and business development partners, connecting our customers with the right expert support at every stage of their public sector journey
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                        {/* <FancyGoldButton to="/register" endIcon={<ArrowForwardIcon />}>
                            Start Free Trial
                        </FancyGoldButton> */}
                        <FancyLightButton onClick={() => setDemoOpen(true)}>
                            Find the Right Partner
                        </FancyLightButton>
                    </Stack>
                </Container>
            </Box>

            <WaveDivider top={`linear-gradient(135deg, ${SOLUTIONS_HERO_START} 0%, ${SOLUTIONS_HERO_MID} 100%)`} bottom="white" inverted />

            {/* ── Partners ─────────────────────────────────────────────────────── */}
            <Box sx={{ backgroundColor: 'white', py: { xs: 6, md: 10 } }}>
                <Container maxWidth="lg">
                    <Stack spacing={8}>
                        <BidPartnersCarousel
                            chipLabel="Bid Writing"
                            title="Bid Partners"
                            description="Specialist bid writing and tender management firms who work with our customers to prepare, write and submit winning public sector bids"
                            partners={activePartners(bidPartners)}
                        />
                        <BidPartnersCarousel
                            chipLabel="Business Development"
                            title="Business Development Partners"
                            description="Strategic BD and growth specialists helping our customers build pipeline, enter new markets and win more public sector work"
                            partners={activePartners(bdPartners)}
                        />
                        
                    </Stack>
                </Container>
            </Box>


            <CtaSection
                onDemoClick={() => setDemoOpen(true)}
                title='Want to join the network?'
                subtitle='If you are a bid writing firm or business development specialist working in the UK public sector, we would love to hear from you'
                showJoinButton={false}
                demobutton='Get in Touch'
            />
            <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
        </Box>
    )
}
